import { useState } from "react";
import "./noi-chart.css";
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import { GetImages } from "../../../utils/GetImages";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

interface NOIChartProps {
  headerTitle?: string;
  dateFilter?: string;
  totalRevenue?: number;
  totalRevenueFormatted?: string;
  totalRevenueRaw?: string;
  netProfit?: number;
  netProfitFormatted?: string;
  netProfitRaw?: string;
  expenseAmount?: number;
  expenseAmountFormatted?: string;
  expenseAmountRaw?: string;
  isLoading?: boolean;
  noiIsEmpty?: boolean;
  timeRanges?: string[];
  onTimeRangeChange?: (newRange: string) => void;
}

export default function NOIChart({
  headerTitle = "NOI From Tenant",
  dateFilter = "Last 12 Month",
  totalRevenue = 351905.18,
  totalRevenueFormatted,
  totalRevenueRaw,
  netProfit = 351905.18,
  netProfitFormatted,
  netProfitRaw,
  expenseAmount = 351905.18,
  expenseAmountFormatted,
  expenseAmountRaw,
  onTimeRangeChange,
  isLoading = false,
  noiIsEmpty = false,
  timeRanges = ["Last 12 Month", "Last 6 Month", "Last 3 Month", "This Month"],
}: NOIChartProps) {
  const [selectedRange, setSelectedRange] = useState(dateFilter);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [tooltipData, setTooltipData] = useState<{ title: string; value: number; formattedValue?: string; valueRaw?: string } | null>(null);
  const currencySymbol = useCurrencySymbol();

  // Mouse move handler
  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    title: string,
    value: number,
    formattedValue?: string,
    valueRaw?: string
  ) => {
    setTooltipPos({ x: e.clientX, y: e.clientY - 10 }); // -10 for slightly above cursor
    setTooltipData({ title, value, formattedValue, valueRaw });
  };

  const handleMouseLeave = () => {
    setTooltipPos(null);
    setTooltipData(null);
  };

  const handleTimeRangeChange = (newRange: string) => {
    setSelectedRange(newRange);
    onTimeRangeChange?.(newRange);
  };

  // Calculate percentages with minimum width for visibility
  const calculatePercentage = (value: number, total: number) => {
    if (!total) return 50;
    const percentage = (value / total) * 100;
    // Minimum 3% width to ensure visibility
    return Math.max(percentage, 3);
  };

  const noiPercentage = calculatePercentage(netProfit, totalRevenue);
  const expensePercentage = calculatePercentage(expenseAmount, totalRevenue);

  const totalPercentage = noiPercentage + expensePercentage;
  const adjustedNoiPercentage = (noiPercentage / totalPercentage) * 100;
  const adjustedExpensePercentage = (expensePercentage / totalPercentage) * 100;


  // Helper to format currency inside the component to match your requested logic
  const formatValue = (val: number) => {
    return val.toLocaleString("en-US", { minimumFractionDigits: 2 });
  };

  return (
    <div className="noi-chart-container">
      <div className="noi-chart-header">
        <h2 className="noi-chart-title">{headerTitle}</h2>
        <TimeRangeDropdown
          timeRanges={timeRanges}
          defaultTimeRange={selectedRange}
          onTimeRangeChange={handleTimeRangeChange}
          className="my-custom-dropdown"
        />
      </div>
      <div className="noi-chart-body">
        {isLoading ? (
          <div className="loading-spinner">
            <SimpleSpinner myClassName={"Icon50 mt-5"} />
          </div>
        ) : noiIsEmpty ? (
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data found"
            isButton={false}
          />
        ) : (
          <>
            <div className="total-revenue-section">
              <div className="diamond-marker total-left-diamond"></div>
              <div className="total-revenue-line left-line"></div>
              <div className="total-revenue-text">
                
                <span>Total Revenue{" "}
                  <CurrencyTooltip
                    content={totalRevenueRaw || totalRevenue}
                    formatted={totalRevenueFormatted}
                  >
                    <span>{totalRevenueFormatted || formatValue(totalRevenue)}</span>
                  </CurrencyTooltip>
                </span>
              </div>
              <div className="total-revenue-line right-line"></div>
              <div className="diamond-marker total-right-diamond"></div>
            </div>

            <div className="chart-container">
              <div className="chart-bar">
                {/* NOI SECTION */}
                <div
                  className="noi-section"
                  style={{ width: `${adjustedNoiPercentage}%` }}
                  onMouseMove={(e) => handleMouseMove(e, "NOI", netProfit, netProfitFormatted, netProfitRaw)}
                  onMouseLeave={handleMouseLeave}
                />

                {/* EXPENSE SECTION */}
                <div
                  className="expense-section"
                  style={{ width: `${adjustedExpensePercentage}%` }}
                  onMouseMove={(e) => handleMouseMove(e, "Expense", expenseAmount, expenseAmountFormatted, expenseAmountRaw)}
                  onMouseLeave={handleMouseLeave}
                />
              </div>
            </div>

            {/* Tooltip - Ab yeh body level par hai, kabhi clip nahi hogi */}
            {tooltipPos && tooltipData && (
              <div
                className="master-tooltip" // Naya class name
                style={{
                  position: "fixed",        // ← Yeh sabse important hai!
                  top: tooltipPos.y,
                  left: tooltipPos.x,
                  transform: "translate(-50%, -100%)", // Center + exactly above cursor
                  pointerEvents: "none",    // Mouse events block na kare
                  zIndex: 9999,
                }}
              >
                <div className="chart-tooltip">
                  <div className="chart-tooltip-content">
                    {tooltipData.title}:{" "}
                    <CurrencyTooltip
                      content={tooltipData.valueRaw || tooltipData.value}
                      formatted={tooltipData.formattedValue}
                  >
                      <span>{tooltipData.value < 0 ? `-${currencySymbol}${formatValue(Math.abs(tooltipData.value))}` : `${currencySymbol}${formatValue(tooltipData.value)}`}</span>
                    </CurrencyTooltip>
                    </div>
                  <div className="chart-tooltip-arrow"></div>
                </div>
              </div>
            )}

            <div className="chart-labels">
              <div className="label-left">
                <div className="diamond-marker bottom-diamond"></div>
                <span className="label-text">
                  NOI{" "}
                   <CurrencyTooltip
                      content={netProfitRaw || netProfit}
                      formatted={netProfitFormatted}
                   >
                     <span>{netProfitFormatted || formatValue(netProfit)}</span>
                   </CurrencyTooltip>
                </span>
              </div>
              <div className="label-right">
                <div className="diamond-marker bottom-diamond"></div>
                <span className="label-text">
                  Expense{" "}
                   <CurrencyTooltip
                        content={expenseAmountRaw || expenseAmount}
                        formatted={expenseAmountFormatted}
                    >
                    <span>{expenseAmountFormatted || formatValue(expenseAmount)}</span>
                   </CurrencyTooltip>
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
